<template lang="pug">
  div
    .form__title
      slot(name='title')
    form.form__body(action='javascript://', v-if='params')
      template(v-if="gameMode !== 'generator-settings'")
        .form__field
          label.form__label
            | {{ $i18n.t('trainer.trainer_gameFormSetting_sequenceOfNumbersMode') }}
          .form__field-body
            cs-multiselect(
              :multiple='false',
              :options='gameModes',
              :show-labels='false',
              :allow-empty='false',
              track-by='label',
              label='label',
              :placeholder="$i18n.t('trainer.trainer_gameFormSetting_chooseGameWithTime')",
              v-model='selectedGameMode'
              @select="gameModeHandler"
            )
        .form__field
          label.form__label
            | {{ $i18n.t('trainer.trainer_gameFormSetting_tableSize') }}
          .form__field-body
            cs-multiselect(
              :multiple='false',
              :options="tableSizes"
              v-model='params.tableSize',
              :allow-empty='false',
              :custom-label="tableSizeLabel"
              :placeholder="tableSizeLabel(params.tableSize)"
            )
        .form__field
          label.form__label
            | {{ getTitleForNumOfExamples }}
          .form__field-body.form-container__select-examples
            cs-spin-input(
              :value="!params.countingCombinations && params.numOfExamples",
              :max="numOfExamples",
              :min="3",
              :disabled="params.countingCombinations",
              @increase="increaseExamples",
              @decrease="decreaseExamples",
              @input="params.numOfExamples = $event"
            )
        .form__field
          .form__field-body.form__field-body_helper.form__field-body--bold
            | {{ getTitleForNumberOfExamplesHelper }}
        divider
        .form__field
          label.form__label
            | {{ $i18n.t('trainer.trainer_gameFormSetting_location') }}:
        .form__field
          label.form__label
            | {{ $i18n.t('trainer.trainer_gameFormSetting_location-vertically') }}
          cs-checkbox.form__field-body(
            v-model="params.location.vertically",
            :checked='params && params.location.vertically',
            :disabled='false'
          )
        .form__field
          label.form__label
            | {{ $i18n.t('trainer.trainer_gameFormSetting_location-horizontally') }}
          cs-checkbox.form__field-body(
            v-model="params.location.horizontally",
            :checked='params && params.location.horizontally',
            :disabled='false'
          )
        .form__field
          label.form__label
            | {{ $i18n.t('trainer.trainer_gameFormSetting_location-diagonally') }}
          cs-checkbox.form__field-body(
            v-model="params.location.diagonally",
            :checked='params && params.location.diagonally',
            :disabled='false'
          )
        divider
        .form__field
          label.form__label
            | {{ getTitleForCrossingSettings }}
          cs-checkbox.form__field-body(
            v-model="params.crossing",
            :checked='params && params.crossing',
            :disabled='false'
          )
        .form__field
          label.form__label
            | {{getTitleForDigitCapacitySettings}}
          .form__field-body
            cs-multiselect(
              :multiple='false',
              :options='digitCapacityModesWithLabel',
              :show-labels='false',
              :allow-empty='false',
              track-by='label',
              label="label",
              :placeholder="$i18n.t('trainer.trainer_gameFormSetting_chooseDifficulty')",
              v-model='selectedDigitCapacity'
              @select="digitCapacityHandler($event.value)"
            )
        .form__field(v-if="!checkIsNumberGameMode")
          label.form__label
            | {{ $i18n.t('trainer.trainer_gameFormSetting_counting-combinations') }}
          cs-checkbox.form__field-body(
            v-model="params.countingCombinations",
            :checked='params && params.countingCombinations',
            :disabled='false'
          )
        .form__field
          label.form__label
            | {{ getTitleForColoredNumbersSettings }}
          cs-checkbox.form__field-body(
            v-model="params.coloredNumbers",
            :checked='params && params.coloredNumbers',
            :disabled='false'
          )
        divider
      slot(name='parameters')
      slot(name='help')
      .form__buttons(v-if='!usersIds')
        cs-button.form__button(@click='cancel', color="blue", pattern="primary-outline")
          | {{ $i18n.t('trainer.trainer_gameFormSetting_dontSave') }}
        template(v-if="gameMode === 'free-game'")
          cs-button.form__button(color="blue", @click='saveGameMode', :disabled="isNeedToDisableSaveButton")
            | {{ $i18n.t('trainer.trainer_gameFormSetting_saveGame') }}
        template(v-else='')
          cs-button.form__button(color="blue", @click='saveGame', :disabled="isNeedToDisableSaveButton")
            | {{ $i18n.t('trainer.trainer_gameFormSetting_saveGame') }}
      .form__buttons(v-else)
        cs-button.form__button(@click='cancel', color="blue", pattern="primary-outline")
          | {{ $i18n.t('trainer.trainer_gameFormSetting_dontSave') }}
        cs-button.form__button(color="blue", @click='saveGame', :disabled="isNeedToDisableSaveButton")
          | {{ $i18n.t('trainer.trainer_gameFormSetting_saveDifference') }}
</template>

<script>
    import GameFormMixin from '../../../utils/trainer/GameFormMixin';
    import TrainerGames from '../../../config/trainer';
    import { getInitParameters } from '../../../utils/trainer/getInitParameters';
    import { mapState } from 'vuex';
    import { cloneDeep } from 'lodash';
    import multiplayerFormMixin from '../../../utils/multiplayer/formMixin';
    import CsCheckbox from '../../../elements/CsCheckbox';
    import SequenceOfNumbersConfig from '../../trainer/configs/SequenceOfNumbersConfig';

    const MIN_DIGIT_CAPACITY = 3;
    const NUMBERS_GAME_MODE = 'numbers';
    const CHARACTERS_GAME_MODE = 'characters';

    export default {
        components: { CsCheckbox },
        props: [
            'slug',
            'courseType',
            'id',
            'usersIds',
            'homeworkId',
            'gameMode',
            'levelAnswerDuration',
            'gameId',
            'generatorSettings',
            'generatorGameIndex',
        ],
        mixins: [GameFormMixin, multiplayerFormMixin],
        data() {
            return {
                digitCapacityModes: SequenceOfNumbersConfig.getDigitCapacityModes(),
                gameModes: [
                    {
                        label: this.$i18n.t('trainer.trainer_gameFormSetting_sequenceOfNumbersModeNumbers'),
                        value: NUMBERS_GAME_MODE,
                    },
                    {
                        label: this.$i18n.t('trainer.trainer_gameFormSetting_sequenceOfNumbersModeCharacters'),
                        value: CHARACTERS_GAME_MODE,
                    },
                ],
                tableSizes: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                numOfExamples: 10,
                params: null,
                selectedDigitCapacity: null,
                selectedGameMode: null,
            };
        },
        watch: {
            'params.mode': {
                handler() {
                    this.digitCapacityHandler(this.params.digitCapacity);
                    if (this.checkIsNumberGameMode) {
                        this.params.countingCombinations = false;
                    }
                },
            },
            'params.tableSize': {
                handler() {
                    if (this.params.digitCapacity > this.params.tableSize) {
                        this.digitCapacityHandler(MIN_DIGIT_CAPACITY);
                    }
                },
            },
            'params.crossing': {
                handler() {
                    if (!this.params.crossing && this.params.digitCapacity < MIN_DIGIT_CAPACITY) {
                        this.digitCapacityHandler(MIN_DIGIT_CAPACITY);
                    }
                },
            },
        },
        mounted() {
            if (this.gameMode === 'free-game') {
                let paramsArray = this.$store.state.trainer.paramsArray;
                if (paramsArray && paramsArray[0] && paramsArray[0].slug === this.slug) {
                    this.paramsArray = cloneDeep(paramsArray);
                    this.$emit('playersNumber', this.paramsArray.length);
                    this.params = this.paramsArray[0];
                } else if (this.gamesParameters) {
                    this.$set(
                        this,
                        'params',
                        getInitParameters({
                            slug: this.slug,
                            parameters: {
                                default: cloneDeep(this.gamesParameters[this.slug]),
                            },
                        })
                    );
                } else {
                    this.params = cloneDeep(TrainerGames.games).find(item => {
                        return item.slug === this.slug && item.courseType === 'math-magic';
                    }).parameters.default;
                }
            } else {
                this.setInitParams(this.getKey(this.usersIds));
            }
            this.selectedGameMode = this.gameModes.find(gameMode => gameMode.value === this.params.mode);
        },
        methods: {
            tableSizeLabel(tableSize) {
                return tableSize + ' x ' + tableSize;
            },
            digitCapacityHandler(digitCapacityValue) {
                this.selectedDigitCapacity = cloneDeep(
                    this.digitCapacityModesWithLabel.find(digitCapacity => digitCapacity.value === digitCapacityValue)
                );
                this.params.digitCapacity = digitCapacityValue;
            },
            gameModeHandler(gameMode) {
                this.selectedGameMode = this.gameModes.find(mode => mode.value === gameMode.value);
                this.params.mode = gameMode.value;
            },
            decreaseExamples() {
                if (this.params.numOfExamples !== 1) {
                    this.params.numOfExamples -= 1;
                }
            },
            increaseExamples() {
                if (this.params.numOfExamples !== this.numOfExamples) {
                    this.params.numOfExamples += 1;
                }
            },
        },
        computed: {
            ...mapState({
                homework: state => state.trainer.homework,
                gamesParameters: state => state.trainer.gamesParameters,
                user: state => state.auth.user,
            }),
            getTitleForDigitCapacitySettings() {
                return this.checkIsNumberGameMode
                    ? this.$i18n.t('trainer.trainer_gameFormSetting_difficulty')
                    : this.$i18n.t('trainer.trainer_gameFormSetting_numOfCharacters');
            },
            getTitleForNumOfExamples() {
                return this.checkIsNumberGameMode
                    ? this.$i18n.t('trainer.trainer_gameFormSetting_amountOfNumbers')
                    : this.$i18n.t('trainer.trainer_gameFormSetting_amountOfCombination');
            },
            getTitleForCrossingSettings() {
                return this.checkIsNumberGameMode
                    ? this.$i18n.t('trainer.trainer_gameFormSetting_crossing-numbers')
                    : this.$i18n.t('trainer.trainer_gameFormSetting_crossing-characters');
            },
            digitCapacityModesWithLabel() {
                return this.digitCapacityModes
                    .map(mode => {
                        mode.label = this.checkIsNumberGameMode
                            ? mode.labelForNumbersMode
                            : mode.labelForCharactersMode;
                        return mode;
                    })
                    .filter(mode => {
                        if (this.params.crossing) {
                            return mode.value <= this.params.tableSize;
                        } else {
                            return mode.value <= this.params.tableSize && mode.value > 2;
                        }
                    });
            },
            getTitleForColoredNumbersSettings() {
                return this.checkIsNumberGameMode
                    ? this.$i18n.t('trainer.trainer_gameFormSetting_coloredNumbers')
                    : this.$i18n.t('trainer.trainer_gameFormSetting_coloredCharacters');
            },
            checkIsNumberGameMode() {
                return this.params.mode === NUMBERS_GAME_MODE;
            },
            isNeedToDisableSaveButton() {
                return (
                    !this.params.location.vertically &&
                    !this.params.location.horizontally &&
                    !this.params.location.diagonally
                );
            },
            getTitleForNumberOfExamplesHelper() {
                return this.checkIsNumberGameMode
                    ? this.$i18n.t('trainer.trainer_gameFormSetting_helperForNumbers')
                    : this.$i18n.t('trainer.trainer_gameFormSetting_helperForCharacters');
            },
        },
    };
</script>

<style lang="scss" scoped>
    .form__title {
        font-weight: 500;
        font-size: 22px;
        line-height: 26px;
        text-align: center;
        margin-bottom: 25px;
    }

    .form__body {
        width: 100%;
        box-sizing: border-box;
        @include media($lg) {
            padding: 0 4.5%;
        }
    }

    .form__field {
        display: flex;
        flex-direction: column;
        margin-bottom: 20px;
        @include media($md) {
            padding: 0 2.5px;
            flex-direction: row;
            align-items: center;
        }
        @include media($lg) {
            padding: 0 10%;
        }
        @include media($xlg) {
            padding: 0 13%;
        }
    }

    .form__field--column {
        flex-direction: column;
        margin-bottom: 20px;
        align-items: flex-start;
    }

    .form__input {
        margin-bottom: 0;
    }

    .form__label {
        min-width: 0;
        font-size: 16px;
        line-height: 19px;
        margin-right: 20px;
        margin-bottom: 0.5rem;
        @include media($md) {
            width: 8em;
            margin-bottom: 0;
            text-align: right;
        }
        @include media($xlg) {
            max-width: none;
            min-width: 13em;
        }
    }

    .form__label--left {
        min-width: 0;
        width: auto;
        margin-bottom: 20px;
    }

    .form__label--image {
        @include media($md) {
            padding-top: 2.5em;
        }
    }

    .form__field-body {
        flex-grow: 5;
    }

    .form__field-body--inline {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
    }

    .form__field-body--bold {
        font-weight: 500;
    }

    .form__field-body_helper {
        font-size: 12px;
        color: #888888;
    }

    .numbers-checkbox:not(:last-child) {
        margin-right: 10px;
    }

    .form__buttons {
        display: flex;
        justify-content: center;
        align-items: center;
        margin-top: 20px;
        flex-direction: column;
        @include media($md) {
            flex-direction: row;
        }
    }

    .form__button {
        width: 200px;
        margin: 20px 0 0 0;
        @include media($md) {
            margin: 0 0 0 20px;
        }
    }

    .form-container__select-examples {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        @include media($md) {
            flex-direction: row;
            align-items: center;
        }
    }

    .form-container__checkbox {
        margin-left: 30px;
    }

    .form-container__svg-icon {
        opacity: 0.7;
        margin: 0 10px 10px 0;
    }
</style>
